export const HIRE_STOCK_PROP_FIELDS = Array.from({ length: 50 }, (_, i) => `prop${i + 1}`);

export const HIRE_STOCK_PROVISIONAL_TYPES = [
  { value: 'BORROWED', label: 'Borrowed' },
  { value: 'CROSS-HIRE', label: 'Cross Hire' },
  { value: 'LEASED', label: 'Leased' },
  { value: 'SALE-STOCK', label: 'Sale Stock' },
];

const PROVISIONAL_TYPE_LOOKUP = new Map(
  HIRE_STOCK_PROVISIONAL_TYPES.flatMap(({ value, label }) => [
    [value.toLowerCase(), value],
    [label.toLowerCase(), value],
    [value.replace(/-/g, ' ').toLowerCase(), value],
    [label.replace(/-/g, ' ').toLowerCase(), value],
  ])
);

export function normalizeProvisionalType(value) {
  if (value == null || value === '') return '';
  const trimmed = String(value).trim();
  const key = trimmed.toLowerCase().replace(/\s+/g, ' ');
  return PROVISIONAL_TYPE_LOOKUP.get(key) || trimmed;
}

export function isKnownProvisionalType(value) {
  if (value == null || value === '') return true;
  return HIRE_STOCK_PROVISIONAL_TYPES.some(
    (option) => option.value === normalizeProvisionalType(value)
  );
}

export const HIRE_STOCK_RATE_FIELDS = [
  'week_rate',
  'one_day_rate',
  'two_day_rate',
  'three_day_rate',
  'four_day_rate',
  'part_day_rate',
  'weekend_rate',
  'hour_rate',
  'day_rate',
  'month_rate',
  'sc_rate',
  'unit_rate',
  'switch_week_rate',
  'deposit',
  'insurance_value',
  'previous_cost_parts',
  'previous_cost_labour',
  'fixed_monthly_cost',
  'anticipated_weekly_income',
];

export const HIRE_STOCK_ZERO_ALLOWED_MONEY_FIELDS = [
  'purch_price',
  'sale_value',
  'previous_income',
];

export const HIRE_STOCK_FINANCIAL_FIELDS = [
  ...HIRE_STOCK_ZERO_ALLOWED_MONEY_FIELDS,
  ...HIRE_STOCK_RATE_FIELDS,
];

export function createEmptyHireStockForm(defaultCategory = '') {
  const form = {
    cat: defaultCategory,
    stock_no: '',
    name: '',
    serial_no: '',
    purch_date: '',
    purch_price: undefined,
    supplier: '',
    additional_info: '',
    depot: '',
    last_meter_rdg: '',
    week_rate: undefined,
    one_day_rate: undefined,
    two_day_rate: undefined,
    three_day_rate: undefined,
    four_day_rate: undefined,
    part_day_rate: undefined,
    weekend_rate: undefined,
    hour_rate: undefined,
    day_rate: undefined,
    month_rate: undefined,
    sc_rate: undefined,
    unit_rate: undefined,
    switch_week_rate: undefined,
    switch_after_weeks: '',
    deposit: undefined,
    sale_date: '',
    sale_value: undefined,
    sale_invoice: '',
    provisional_type: '',
    reg_number: '',
    nom_code: '',
    depreciation_rate: '',
    insurance_value: undefined,
    make: '',
    model: '',
    weight: '',
    hire_notes: '',
    previous_income: undefined,
    previous_cost_parts: undefined,
    previous_cost_labour: undefined,
    inspection_test: '',
    warranty_type: '',
    warranty_supplier: '',
    warranty_start_date: '',
    barcode: '',
    rfid: '',
    alert_text: '',
    fixed_monthly_cost: undefined,
    anticipated_weekly_income: undefined,
    on_hp: false,
    hp_notes: '',
    hp_over_months: '',
    qr_code: '',
    active: true,
  };

  HIRE_STOCK_PROP_FIELDS.forEach((field) => {
    form[field] = '';
  });

  return form;
}

export const HIRE_STOCK_DATE_FIELDS = ['purch_date', 'sale_date', 'warranty_start_date'];

export const HIRE_STOCK_INTEGER_FIELDS = ['last_meter_rdg', 'switch_after_weeks', 'hp_over_months'];

export const HIRE_STOCK_STRING_FIELDS = [
  'cat',
  'stock_no',
  'name',
  'serial_no',
  'supplier',
  'additional_info',
  'depot',
  'sale_invoice',
  'provisional_type',
  'reg_number',
  'nom_code',
  'depreciation_rate',
  'make',
  'model',
  'hire_notes',
  'inspection_test',
  'warranty_type',
  'warranty_supplier',
  'barcode',
  'rfid',
  'alert_text',
  'hp_notes',
  'qr_code',
  ...HIRE_STOCK_PROP_FIELDS,
];

export function formatDateForInput(value) {
  if (value == null || value === '') return '';
  const str = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(str)) {
    return str.slice(0, 10);
  }
  const ukMatch = str.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  if (ukMatch) {
    const [, day, month, year] = ukMatch;
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  }
  const parsed = new Date(str);
  if (Number.isNaN(parsed.getTime())) {
    return '';
  }
  return parsed.toISOString().slice(0, 10);
}

export function formatNumberForInput(value) {
  if (value == null || value === '') return '';
  return String(value);
}

export function isValueInOptions(value, options, getOptionValue) {
  if (value == null || value === '') return true;
  const normalized = String(value).trim();
  return options.some((option) => String(getOptionValue(option)).trim() === normalized);
}

export function cleanHireStockFinancialValues(data) {
  const cleaned = { ...data };
  HIRE_STOCK_FINANCIAL_FIELDS.forEach((field) => {
    if (cleaned[field] === 0 || cleaned[field] === '0' || cleaned[field] === '0.00') {
      cleaned[field] = undefined;
    }
  });
  return cleaned;
}

export function normalizeHireStockFormData(data) {
  const cleaned = cleanHireStockFinancialValues({
    ...createEmptyHireStockForm(),
    ...data,
  });

  HIRE_STOCK_DATE_FIELDS.forEach((field) => {
    cleaned[field] = formatDateForInput(cleaned[field]);
  });

  HIRE_STOCK_STRING_FIELDS.forEach((field) => {
    if (cleaned[field] == null) {
      cleaned[field] = '';
    } else if (typeof cleaned[field] === 'string') {
      cleaned[field] = cleaned[field].trim();
    } else {
      cleaned[field] = String(cleaned[field]).trim();
    }
  });

  HIRE_STOCK_PROP_FIELDS.forEach((field) => {
    if (cleaned[field] == null) {
      cleaned[field] = '';
    } else {
      cleaned[field] = String(cleaned[field]).trim();
    }
  });

  [...HIRE_STOCK_FINANCIAL_FIELDS, 'weight'].forEach((field) => {
    if (cleaned[field] != null && cleaned[field] !== '') {
      cleaned[field] = formatNumberForInput(cleaned[field]);
    }
  });

  HIRE_STOCK_INTEGER_FIELDS.forEach((field) => {
    if (cleaned[field] == null || cleaned[field] === '') {
      cleaned[field] = '';
    } else {
      cleaned[field] = String(cleaned[field]).trim();
    }
  });

  if (cleaned.provisional_type) {
    cleaned.provisional_type = normalizeProvisionalType(cleaned.provisional_type);
  }

  if (cleaned.on_hp == null) {
    cleaned.on_hp = false;
  }

  if (cleaned.active == null) {
    cleaned.active = true;
  }

  return cleaned;
}

export function buildHireStockSavePayload(formData) {
  const template = createEmptyHireStockForm();
  const payload = {};

  Object.keys(template).forEach((key) => {
    payload[key] = formData[key] !== undefined ? formData[key] : template[key];
  });

  return payload;
}
